import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSeriesCategories, getSeriesList } from '../services/api';
import { toast } from 'sonner';
import { Search, X, MoreVertical, Play, Tv } from 'lucide-react';

export default function SeriesPage() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [series, setSeries] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [showSearch, setShowSearch] = useState(false);

  useEffect(() => {
    getSeriesCategories()
      .then(setCategories)
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { limit: 100, skip: 0 };
    if (selectedCategory !== 'all') params.category_id = selectedCategory;
    if (searchQuery) params.search = searchQuery;

    getSeriesList(params)
      .then(setSeries)
      .catch((err) => {
        console.error(err);
        toast.error('Could not load series.');
        setSeries([]);
      })
      .finally(() => setLoading(false));
  }, [selectedCategory, searchQuery]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput.trim());
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
  };

  const getRatingBadge = (item) => {
    const r = parseFloat(item.rating || (item.rating_5based ? item.rating_5based * 2 : 0));
    if (!r || isNaN(r) || r < 1) return null;
    return Math.round(r);
  };

  const currentCategoryLabel = selectedCategory === 'all'
    ? 'ALL SERIES'
    : (categories.find(c => c.category_id === selectedCategory)?.category_name || 'SERIES').toUpperCase();

  return (
    <div className="vod-page" data-testid="series-page">
      {/* Left Sidebar — unchanged */}
      <aside className="vod-sidebar" data-testid="series-sidebar">
        <div className="sidebar-header">
          <div className="xtream-logo">
            <img src="/logo.png" alt="FTA Xtream" style={{height:'36px', width:'auto', objectFit:'contain'}} />
          </div>
        </div>

        <nav className="sidebar-nav" data-testid="series-category-list">
          <button
            className={`nav-item ${selectedCategory === 'all' ? 'active' : ''}`}
            onClick={() => setSelectedCategory('all')}
            data-testid="category-all"
          >
            <span className="nav-item-name">ALL SERIES</span>
            <span className="nav-item-count">{series.length}</span>
          </button>
          {categories.map((cat) => (
            <button
              key={cat.category_id}
              className={`nav-item ${selectedCategory === cat.category_id ? 'active' : ''}`}
              onClick={() => setSelectedCategory(cat.category_id)}
              data-testid={`category-${cat.category_id}`}
            >
              <span className="nav-item-name">{cat.category_name.toUpperCase()}</span>
            </button>
          ))}
        </nav>
      </aside>

      {/* Main Content */}
      <main className="vod-main" data-testid="series-main">
        {/* Top Bar */}
        <div className="vod-top-bar">
          <div className="vod-top-spacer" />
          <h1 className="vod-category-title" data-testid="series-category-title">
            {currentCategoryLabel}
          </h1>
          <div className="vod-top-actions">
            <button
              className="vod-icon-btn"
              onClick={() => setShowSearch(s => !s)}
              data-testid="series-search-toggle"
            >
              <Search className="w-5 h-5" />
            </button>
            <button className="vod-icon-btn" data-testid="series-more-btn">
              <MoreVertical className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Collapsible Search */}
        {showSearch && (
          <div className="vod-search-bar">
            <form onSubmit={handleSearch} className="vod-search-form">
              <Search className="w-4 h-4" />
              <input
                type="text"
                placeholder="Search series..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="vod-search-input"
                autoFocus
                data-testid="series-search-input"
              />
              {searchInput && (
                <button type="button" onClick={clearSearch} className="search-clear">
                  <X className="w-4 h-4" />
                </button>
              )}
            </form>
          </div>
        )}

        {/* Grid */}
        <div className="vod-grid-scroll">
          {loading ? (
            <div className="vod-loading" data-testid="series-loading">
              <div className="loading-spinner" />
              <span>Loading series...</span>
            </div>
          ) : series.length === 0 ? (
            <div className="vod-empty" data-testid="series-empty">
              <Tv className="w-14 h-14 mb-3" style={{opacity:0.2}} />
              <p>No series found. Log in with your IPTV credentials to access content.</p>
            </div>
          ) : (
            <div className="vod-grid" data-testid="series-grid">
              {series.map((item) => {
                const badge = getRatingBadge(item);
                const year = item.releaseDate ? item.releaseDate.substring(0, 4) : '';
                const label = item.name + (year ? ` - ${year}` : '');
                return (
                  <div
                    key={item.series_id}
                    className="vod-card"
                    onClick={() => navigate(`/series/${item.series_id}`)}
                    data-testid={`series-card-${item.series_id}`}
                  >
                    <div className="vod-card-poster">
                      {item.cover ? (
                        <img src={item.cover} alt={item.name} loading="lazy" />
                      ) : (
                        <div className="vod-card-no-poster">
                          <Play className="w-8 h-8" style={{opacity:0.2}} />
                        </div>
                      )}
                      {badge && (
                        <div className="vod-rating-badge" data-testid={`series-rating-${item.series_id}`}>
                          {badge}
                        </div>
                      )}
                      <div className="vod-card-title-overlay">
                        <span>{label}</span>
                      </div>
                      <div className="vod-card-hover">
                        <div className="vod-play-circle">
                          <Play className="w-6 h-6" style={{marginLeft:'3px'}} />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
